/**
 * Voucher Composer V2 · Editor Shell.
 *
 * Wraps the canonical <VoucherDesignRenderer> in edit mode and adds:
 *   • Preset row (Royal Gold, Emerald Club, Midnight Premium, ...)
 *   • Right-side property panel (per element type)
 *   • Layers panel with reorder / hide / lock / rename / delete
 *   • Local undo/redo history stack (session only)
 *   • Add-element menu (custom text, rectangle, circle, line, chip, image)
 *
 * This component is Admin-only. The Guest Wallet uses
 * <VoucherDesignRenderer mode="read"> directly and never mounts any of
 * the editor code below.
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Undo2, Redo2, Trash2, Copy, ArrowUpFromLine, ArrowDownToLine,
  Eye, EyeOff, Lock, Unlock, AlignHorizontalJustifyCenter, AlignVerticalJustifyCenter,
  Type, Square, Circle, Minus, Image as ImageIcon, Award, Plus,
  Sparkles, Ribbon, Star, Crown, Gift, Utensils, Cake, Wine, Coffee,
  Heart, Ticket, Calendar, PartyPopper, Trophy, Diamond, Flame,
  Bookmark, Zap,
  Hotel as LucideHotel, Bed as LucideBed, Flower2 as LucideFlower2,
  ChefHat as LucideChefHat, Music as LucideMusic, Percent as LucidePercent,
  IndianRupee as LucideIndianRupee,
} from 'lucide-react';
import VoucherDesignRenderer, { FUNCTIONAL_IDS, UNDELETABLE_IDS, clampElement } from './VoucherDesignRenderer';
import { COMPOSER_PRESETS, migrateLegacyToV2, newElementId, ASPECT_RATIOS, BLANK_PRESET } from './voucherComposerPresets';

// ── Dynamic-field factories (bindings). Phase 2A · every binding
// can be added, removed, moved, duplicated. They are NOT forced onto
// any canvas — admin decides which appear.
const makeDynamic = {
  brand:     () => ({ id: 'brand',     type: 'brand',
    x: 0.06, y: 0.08, width: 0.55, height: 0.09, rotation: 0, z_index: 30, opacity: 1, locked: false,
    style: { color: '#F7F5EE', font: 'Montserrat', weight: 700, size: 0.036, letter_spacing: '0.06em', align: 'left' },
    content: { show_tagline: true } }),
  logo:      () => ({ id: 'logo',      type: 'logo',
    x: 0.80, y: 0.06, width: 0.14, height: 0.22, rotation: 0, z_index: 31, opacity: 1, locked: false,
    style: { fit: 'contain', border_radius: 999, background: 'transparent' }, content: {} }),
  title:     () => ({ id: 'title',     type: 'title',
    x: 0.08, y: 0.36, width: 0.84, height: 0.11, rotation: 0, z_index: 32, opacity: 1, locked: false,
    style: { color: '#F7F5EE', font: 'Playfair Display', weight: 700, size: 0.058, align: 'center', letter_spacing: '0.02em' },
    content: {} }),
  subtitle:  () => ({ id: 'subtitle',  type: 'subtitle',
    x: 0.10, y: 0.48, width: 0.80, height: 0.06, rotation: 0, z_index: 33, opacity: 1, locked: false,
    style: { color: 'rgba(247,245,238,0.75)', font: 'Inter', weight: 500, size: 0.024, align: 'center', letter_spacing: '0.15em', text_transform: 'uppercase' },
    content: {} }),
  value:     () => ({ id: 'value',     type: 'value',
    x: 0.08, y: 0.58, width: 0.84, height: 0.18, rotation: 0, z_index: 34, opacity: 1, locked: false,
    style: { color: '#C9A227', font: 'Playfair Display', weight: 800, size: 0.11, align: 'center' },
    content: {} }),
  qr:        () => ({ id: 'qr',        type: 'qr',
    x: 0.08, y: 0.72, width: 0.18, height: 0.28, rotation: 0, z_index: 35, opacity: 1, locked: false,
    style: { fg: '#0E2C22', bg: '#F7F5EE', border_radius: 12, padding: 0.012 }, content: {} }),
  code:      () => ({ id: 'code',      type: 'code',
    x: 0.34, y: 0.76, width: 0.58, height: 0.06, rotation: 0, z_index: 36, opacity: 1, locked: false,
    style: { color: '#F7F5EE', font: 'JetBrains Mono, monospace', weight: 600, size: 0.032, align: 'left', letter_spacing: '0.14em' },
    content: {} }),
  validity:  () => ({ id: 'validity',  type: 'validity',
    x: 0.34, y: 0.85, width: 0.58, height: 0.05, rotation: 0, z_index: 37, opacity: 1, locked: false,
    style: { color: 'rgba(247,245,238,0.72)', font: 'Inter', weight: 500, size: 0.022, align: 'left', letter_spacing: '0.08em' },
    content: {} }),
  terms_cta: () => ({ id: 'terms_cta', type: 'terms_cta',
    x: 0.34, y: 0.91, width: 0.58, height: 0.045, rotation: 0, z_index: 38, opacity: 1, locked: false,
    style: { color: 'rgba(247,245,238,0.55)', font: 'Inter', weight: 400, size: 0.020, align: 'left', text_decoration: 'underline' },
    content: { text: 'Terms & Conditions' } }),
};
const DYNAMIC_FIELD_ORDER = ['brand', 'logo', 'title', 'subtitle', 'value', 'qr', 'code', 'validity', 'terms_cta'];
const DYNAMIC_FIELD_LABELS = {
  brand: 'Brand Name', logo: 'Brand Logo', title: 'Voucher Name',
  subtitle: 'Subtitle', value: 'Offer / Value', qr: 'QR Code',
  code: 'Voucher Code', validity: 'Validity', terms_cta: 'Terms CTA',
};

// ── Element type registry (add-element menu) ─────────────────
const ADDABLE = [
  { type: 'text',      label: 'Custom Text',    Icon: Type,       factory: () => ({
    id: newElementId('text'), type: 'text',
    x: 0.20, y: 0.45, width: 0.60, height: 0.06,
    rotation: 0, z_index: 30, opacity: 1, locked: false,
    style: { color: '#F7F5EE', font: 'Inter', weight: 500, size: 0.028, align: 'center' },
    content: { text: 'Custom text' },
  }) },
  { type: 'rectangle', label: 'Rectangle',      Icon: Square,     factory: () => ({
    id: newElementId('rect'), type: 'rectangle',
    x: 0.20, y: 0.45, width: 0.30, height: 0.08,
    rotation: 0, z_index: 25, opacity: 1, locked: false,
    style: { fill: '#C9A227', border_radius: 8, stroke: 'transparent', stroke_width: 0 },
    content: {},
  }) },
  { type: 'circle',    label: 'Circle',         Icon: Circle,     factory: () => ({
    id: newElementId('circle'), type: 'circle',
    x: 0.35, y: 0.42, width: 0.15, height: 0.09,
    rotation: 0, z_index: 25, opacity: 1, locked: false,
    style: { fill: '#C9A227', stroke: 'transparent', stroke_width: 0 },
    content: {},
  }) },
  { type: 'line',      label: 'Line / Divider', Icon: Minus,      factory: () => ({
    id: newElementId('line'), type: 'line',
    x: 0.10, y: 0.50, width: 0.80, height: 0.002,
    rotation: 0, z_index: 25, opacity: 1, locked: false,
    style: { color: '#C9A227', thickness: 1.4, dash: 'solid' },
    content: {},
  }) },
  { type: 'chip',      label: 'Chip / Badge',   Icon: Award,      factory: () => ({
    id: newElementId('chip'), type: 'chip',
    x: 0.35, y: 0.06, width: 0.30, height: 0.06,
    rotation: 0, z_index: 40, opacity: 1, locked: false,
    style: { color: '#0E2C22', bg: '#C9A227', font: 'Inter', weight: 700, size: 0.020,
             align: 'center', letter_spacing: '0.20em', text_transform: 'uppercase',
             border_radius: 999, padding_x: 0.02, padding_y: 0.008 },
    content: { text: 'PREMIUM' },
  }) },
  { type: 'image',     label: 'Image',          Icon: ImageIcon,  factory: () => ({
    id: newElementId('image'), type: 'image',
    x: 0.35, y: 0.42, width: 0.30, height: 0.20,
    rotation: 0, z_index: 20, opacity: 1, locked: false,
    style: { fit: 'cover', border_radius: 8 },
    content: { url: '' },
  }) },
  // ── Ribbons ────────────────────────────────────────────────
  { type: 'ribbon_horizontal', label: 'Ribbon · Banner', Icon: Ribbon, factory: () => ({
    id: newElementId('ribbon'), type: 'ribbon',
    x: 0.05, y: 0.18, width: 0.90, height: 0.10,
    rotation: 0, z_index: 30, opacity: 1, locked: false,
    style: { fill: '#C9A227', variant: 'banner', color: '#0E2C22',
             stroke: 'rgba(0,0,0,0.25)', font: 'Inter', weight: 800 },
    content: { text: 'LIMITED OFFER' },
  }) },
  { type: 'ribbon_corner',     label: 'Ribbon · Corner', Icon: Ribbon, factory: () => ({
    id: newElementId('ribbon_c'), type: 'ribbon',
    x: 0.72, y: 0.02, width: 0.26, height: 0.16,
    rotation: 0, z_index: 32, opacity: 1, locked: false,
    style: { fill: '#B22222', variant: 'corner', color: '#F7F5EE',
             stroke: 'rgba(0,0,0,0.25)' },
    content: { text: 'NEW' },
  }) },
  { type: 'ribbon_tail',       label: 'Ribbon · Tail',   Icon: Ribbon, factory: () => ({
    id: newElementId('ribbon_t'), type: 'ribbon',
    x: 0.08, y: 0.18, width: 0.60, height: 0.10,
    rotation: 0, z_index: 32, opacity: 1, locked: false,
    style: { fill: '#0E2C22', variant: 'tail', color: '#C9A227',
             stroke: 'rgba(201,162,39,0.4)' },
    content: { text: 'PREMIUM' },
  }) },
  // ── Badges / Seals ──────────────────────────────────────────
  { type: 'badge_seal',   label: 'Badge · Seal',   Icon: Award,  factory: () => ({
    id: newElementId('badge'), type: 'badge',
    x: 0.72, y: 0.42, width: 0.18, height: 0.18 * 1.586,
    rotation: 0, z_index: 40, opacity: 1, locked: false,
    style: { bg: '#C9A227', color: '#0E2C22', shape: 'seal',
             stroke: 'rgba(0,0,0,0.35)', font: 'Inter', weight: 800 },
    content: { text: 'VIP' },
  }) },
  { type: 'badge_shield', label: 'Badge · Shield', Icon: Bookmark, factory: () => ({
    id: newElementId('badge_sh'), type: 'badge',
    x: 0.72, y: 0.42, width: 0.16, height: 0.16 * 1.586,
    rotation: 0, z_index: 40, opacity: 1, locked: false,
    style: { bg: '#0E2C22', color: '#C9A227', shape: 'shield' },
    content: { text: 'EXCL' },
  }) },
  { type: 'badge_star',   label: 'Badge · Star',   Icon: Star,   factory: () => ({
    id: newElementId('badge_st'), type: 'badge',
    x: 0.72, y: 0.42, width: 0.18, height: 0.18 * 1.586,
    rotation: 0, z_index: 40, opacity: 1, locked: false,
    style: { bg: '#C9A227', color: '#0E2C22', shape: 'star' },
    content: { text: 'NEW' },
  }) },
  // ── Icons ───────────────────────────────────────────────────
  { type: 'icon_crown',    label: 'Icon · Crown',    Icon: Crown,       factory: () => makeIcon('Crown') },
  { type: 'icon_star',     label: 'Icon · Star',     Icon: Star,        factory: () => makeIcon('Star') },
  { type: 'icon_gift',     label: 'Icon · Gift',     Icon: Gift,        factory: () => makeIcon('Gift') },
  { type: 'icon_sparkles', label: 'Icon · Sparkles', Icon: Sparkles,    factory: () => makeIcon('Sparkles') },
  { type: 'icon_utensils', label: 'Icon · Dining',   Icon: Utensils,    factory: () => makeIcon('Utensils') },
  { type: 'icon_cake',     label: 'Icon · Cake',     Icon: Cake,        factory: () => makeIcon('Cake') },
  { type: 'icon_wine',     label: 'Icon · Wine',     Icon: Wine,        factory: () => makeIcon('Wine') },
  { type: 'icon_coffee',   label: 'Icon · Coffee',   Icon: Coffee,      factory: () => makeIcon('Coffee') },
  { type: 'icon_heart',    label: 'Icon · Heart',    Icon: Heart,       factory: () => makeIcon('Heart') },
  { type: 'icon_ticket',   label: 'Icon · Ticket',   Icon: Ticket,      factory: () => makeIcon('Ticket') },
  { type: 'icon_cal',      label: 'Icon · Calendar', Icon: Calendar,    factory: () => makeIcon('Calendar') },
  { type: 'icon_party',    label: 'Icon · Party',    Icon: PartyPopper, factory: () => makeIcon('PartyPopper') },
  { type: 'icon_trophy',   label: 'Icon · Trophy',   Icon: Trophy,      factory: () => makeIcon('Trophy') },
  { type: 'icon_diamond',  label: 'Icon · Diamond',  Icon: Diamond,     factory: () => makeIcon('Diamond') },
  { type: 'icon_flame',    label: 'Icon · Flame',    Icon: Flame,       factory: () => makeIcon('Flame') },
  // Additional hospitality icons.
  { type: 'icon_hotel',    label: 'Icon · Hotel',    Icon: LucideHotel,      factory: () => makeIcon('Hotel') },
  { type: 'icon_bed',      label: 'Icon · Bed',      Icon: LucideBed,        factory: () => makeIcon('Bed') },
  { type: 'icon_spa',      label: 'Icon · Spa',      Icon: LucideFlower2,    factory: () => makeIcon('Flower2') },
  { type: 'icon_chef',     label: 'Icon · Chef',     Icon: LucideChefHat,    factory: () => makeIcon('ChefHat') },
  { type: 'icon_music',    label: 'Icon · Music',    Icon: LucideMusic,      factory: () => makeIcon('Music') },
  { type: 'icon_percent',  label: 'Icon · Percent',  Icon: LucidePercent,    factory: () => makeIcon('Percent') },
  { type: 'icon_rupee',    label: 'Icon · Rupee',    Icon: LucideIndianRupee,factory: () => makeIcon('IndianRupee') },
  // ── Effects ────────────────────────────────────────────────
  { type: 'effect_shimmer', label: 'Effect · Shimmer', Icon: Zap, factory: () => ({
    id: newElementId('effect'), type: 'effect',
    x: 0.0, y: 0.30, width: 1.0, height: 0.20,
    rotation: 0, z_index: 5, opacity: 0.8, locked: false,
    style: { variant: 'shimmer', color: 'rgba(255,255,255,0.55)' },
    content: {},
  }) },
  { type: 'effect_glow',    label: 'Effect · Soft Glow', Icon: Sparkles, factory: () => ({
    id: newElementId('effect_g'), type: 'effect',
    x: 0.30, y: 0.20, width: 0.40, height: 0.60,
    rotation: 0, z_index: 3, opacity: 1, locked: false,
    style: { variant: 'glow', color: 'rgba(255,215,120,0.55)' },
    content: {},
  }) },
  // ── Shapes ─────────────────────────────────────────────────
  { type: 'shape_triangle', label: 'Shape · Triangle', Icon: Square, factory: () => ({
    id: newElementId('shape'), type: 'shape',
    x: 0.35, y: 0.42, width: 0.16, height: 0.16,
    rotation: 0, z_index: 22, opacity: 1, locked: false,
    style: { variant: 'triangle', fill: '#C9A227', stroke: 'transparent', stroke_width: 0 },
    content: {},
  }) },
  { type: 'shape_ellipse', label: 'Shape · Ellipse', Icon: Circle, factory: () => ({
    id: newElementId('shape'), type: 'shape',
    x: 0.30, y: 0.40, width: 0.30, height: 0.12,
    rotation: 0, z_index: 22, opacity: 1, locked: false,
    style: { variant: 'ellipse', fill: '#C9A227', stroke: 'transparent', stroke_width: 0 },
    content: {},
  }) },
  { type: 'shape_pill', label: 'Shape · Pill', Icon: Square, factory: () => ({
    id: newElementId('shape'), type: 'shape',
    x: 0.30, y: 0.45, width: 0.30, height: 0.06,
    rotation: 0, z_index: 22, opacity: 1, locked: false,
    style: { variant: 'pill', fill: '#C9A227', stroke: 'transparent', stroke_width: 0 },
    content: {},
  }) },
  { type: 'shape_polygon', label: 'Shape · Hexagon', Icon: Square, factory: () => ({
    id: newElementId('shape'), type: 'shape',
    x: 0.35, y: 0.40, width: 0.16, height: 0.16,
    rotation: 0, z_index: 22, opacity: 1, locked: false,
    style: { variant: 'polygon', sides: 6, fill: '#C9A227', stroke: 'transparent', stroke_width: 0 },
    content: {},
  }) },
  { type: 'shape_diagonal', label: 'Shape · Diagonal Block', Icon: Square, factory: () => ({
    id: newElementId('shape'), type: 'shape',
    x: 0, y: 0.35, width: 0.55, height: 0.55,
    rotation: 0, z_index: 4, opacity: 1, locked: false,
    style: { variant: 'diagonal_block', fill: '#D4A44A', fill2: '#8B5E1E', stroke: 'transparent', stroke_width: 0 },
    content: {},
  }) },
  { type: 'shape_arc', label: 'Shape · Arc', Icon: Square, factory: () => ({
    id: newElementId('shape'), type: 'shape',
    x: 0.10, y: 0.55, width: 0.80, height: 0.20,
    rotation: 0, z_index: 4, opacity: 1, locked: false,
    style: { variant: 'arc', fill: '#C9A227', stroke: 'transparent', stroke_width: 0 },
    content: {},
  }) },
  { type: 'shape_wave', label: 'Shape · Wave', Icon: Square, factory: () => ({
    id: newElementId('shape'), type: 'shape',
    x: 0, y: 0.70, width: 1.0, height: 0.10,
    rotation: 0, z_index: 4, opacity: 1, locked: false,
    style: { variant: 'wave', fill: '#C9A227', stroke: 'transparent', stroke_width: 0 },
    content: {},
  }) },
  // ── Bow (gift-voucher decorative) ──────────────────────────
  { type: 'bow_gold', label: 'Bow · Gold', Icon: Gift, factory: () => ({
    id: newElementId('bow'), type: 'bow',
    x: 0.36, y: 0.30, width: 0.28, height: 0.42,
    rotation: 0, z_index: 40, opacity: 1, locked: false,
    style: { fill: '#D4A44A', fill2: '#8B5E1E', stroke: 'rgba(0,0,0,0.30)' },
    content: {},
  }) },
  // ── Ribbons (extended variants) ─────────────────────────────
  { type: 'ribbon_folded', label: 'Ribbon · Folded', Icon: Ribbon, factory: () => ({
    id: newElementId('ribbon_f'), type: 'ribbon',
    x: 0.08, y: 0.18, width: 0.84, height: 0.13,
    rotation: 0, z_index: 30, opacity: 1, locked: false,
    style: { fill: '#D4AF37', fill2: '#8B5E1E', variant: 'folded', color: '#0E2C22',
             stroke: 'rgba(0,0,0,0.30)', font: 'Playfair Display', weight: 800 },
    content: { text: 'GIFT VOUCHER' },
  }) },
  { type: 'ribbon_festival', label: 'Ribbon · Festival', Icon: Ribbon, factory: () => ({
    id: newElementId('ribbon_fs'), type: 'ribbon',
    x: 0.10, y: 0.12, width: 0.80, height: 0.16,
    rotation: 0, z_index: 30, opacity: 1, locked: false,
    style: { fill: '#B22222', fill2: '#7A0F17', variant: 'festival', color: '#F7F5EE',
             stroke: 'rgba(0,0,0,0.30)', font: 'Playfair Display', weight: 800 },
    content: { text: 'CELEBRATE' },
  }) },
  { type: 'ribbon_luxury', label: 'Ribbon · Luxury Strip', Icon: Ribbon, factory: () => ({
    id: newElementId('ribbon_lx'), type: 'ribbon',
    x: 0.05, y: 0.44, width: 0.90, height: 0.08,
    rotation: 0, z_index: 30, opacity: 1, locked: false,
    style: { fill: '#D4AF37', fill2: '#A87A26', variant: 'luxury_strip', color: '#0E2C22',
             stroke: 'rgba(0,0,0,0.35)', font: 'Cormorant Garamond', weight: 700 },
    content: { text: 'LUXURY' },
  }) },
  { type: 'ribbon_sash',    label: 'Ribbon · Sash',    Icon: Ribbon, factory: () => ({
    id: newElementId('ribbon_s'), type: 'ribbon',
    x: 0.08, y: 0.42, width: 0.84, height: 0.10,
    rotation: 0, z_index: 30, opacity: 1, locked: false,
    style: { fill: '#0E2C22', fill2: '#1A4A38', variant: 'sash', color: '#C9A227',
             stroke: 'rgba(201,162,39,0.35)', font: 'Playfair Display', weight: 800 },
    content: { text: 'EXCLUSIVE' },
  }) },
  { type: 'ribbon_pennant', label: 'Ribbon · Pennant', Icon: Ribbon, factory: () => ({
    id: newElementId('ribbon_p'), type: 'ribbon',
    x: 0.05, y: 0.15, width: 0.42, height: 0.11,
    rotation: 0, z_index: 30, opacity: 1, locked: false,
    style: { fill: '#D4AF37', variant: 'pennant', color: '#0E2C22',
             stroke: 'rgba(0,0,0,0.30)', font: 'Inter', weight: 800 },
    content: { text: 'NEW' },
  }) },
  // ── Badges (extended) ──────────────────────────────────────
  { type: 'badge_starburst', label: 'Badge · Starburst', Icon: Star, factory: () => ({
    id: newElementId('badge_sb'), type: 'badge',
    x: 0.72, y: 0.40, width: 0.20, height: 0.32,
    rotation: 0, z_index: 45, opacity: 1, locked: false,
    style: { bg: '#B22222', bg2: '#7A0F17', color: '#F7F5EE', shape: 'starburst',
             stroke: 'rgba(0,0,0,0.35)', font: 'Inter', weight: 800 },
    content: { text: 'LIMITED' },
  }) },
  { type: 'badge_pill', label: 'Badge · Pill', Icon: Bookmark, factory: () => ({
    id: newElementId('badge_p'), type: 'badge',
    x: 0.65, y: 0.06, width: 0.30, height: 0.08,
    rotation: 0, z_index: 45, opacity: 1, locked: false,
    style: { bg: '#D4AF37', bg2: '#A87A26', color: '#0E2C22', shape: 'pill',
             stroke: 'transparent', font: 'Inter', weight: 800,
             size: 0.020, letter_spacing: '0.20em', text_transform: 'uppercase' },
    content: { text: 'PREMIUM' },
  }) },
  // ── Effects (extended) ────────────────────────────────────
  { type: 'effect_shine', label: 'Effect · Shine strip', Icon: Zap, factory: () => ({
    id: newElementId('effect_sh'), type: 'effect',
    x: 0, y: 0.28, width: 1.0, height: 0.24, rotation: -18,
    z_index: 6, opacity: 0.9, locked: false,
    style: { variant: 'shine_strip', color: 'rgba(255,255,255,0.45)' },
    content: {},
  }) },
  { type: 'effect_shadow', label: 'Effect · Vignette', Icon: Zap, factory: () => ({
    id: newElementId('effect_v'), type: 'effect',
    x: 0, y: 0, width: 1.0, height: 1.0,
    rotation: 0, z_index: 3, opacity: 1, locked: false,
    style: { variant: 'shadow', opacity: 0.65 },
    content: {},
  }) },
  { type: 'effect_glass', label: 'Effect · Glass panel', Icon: Zap, factory: () => ({
    id: newElementId('effect_gl'), type: 'effect',
    x: 0.10, y: 0.40, width: 0.80, height: 0.22,
    rotation: 0, z_index: 26, opacity: 1, locked: false,
    style: { variant: 'glass', border_radius: 16 },
    content: {},
  }) },
  { type: 'effect_blob', label: 'Effect · Blur Blob', Icon: Sparkles, factory: () => ({
    id: newElementId('effect_bl'), type: 'effect',
    x: 0.20, y: 0.20, width: 0.40, height: 0.50,
    rotation: 0, z_index: 3, opacity: 0.7, locked: false,
    style: { variant: 'blur_blob', color: 'rgba(212,175,55,0.55)' },
    content: {},
  }) },
  { type: 'effect_ggl', label: 'Effect · Gradient Glow', Icon: Sparkles, factory: () => ({
    id: newElementId('effect_gg'), type: 'effect',
    x: 0.15, y: 0.15, width: 0.70, height: 0.55,
    rotation: 0, z_index: 4, opacity: 0.85, locked: false,
    style: { variant: 'gradient_glow', color: 'rgba(212,175,55,0.55)', color2: 'rgba(212,175,55,0.10)' },
    content: {},
  }) },
  { type: 'effect_metal', label: 'Effect · Metallic sheen', Icon: Sparkles, factory: () => ({
    id: newElementId('effect_m'), type: 'effect',
    x: 0.06, y: 0.06, width: 0.88, height: 0.88,
    rotation: 0, z_index: 7, opacity: 0.55, locked: false,
    style: { variant: 'metallic', color: 'rgba(212,175,55,0.65)', color2: 'rgba(139,94,30,0.65)', border_radius: 16 },
    content: {},
  }) },
];

// Icon factory reused by every icon entry above.
function makeIcon(name) {
  return {
    id: newElementId('icon'), type: 'icon',
    x: 0.06, y: 0.06, width: 0.09, height: 0.09 * 1.586,
    rotation: 0, z_index: 25, opacity: 1, locked: false,
    style: { color: '#C9A227', stroke_width: 1.5 },
    content: { name },
  };
}

// Grouping metadata for the Add menu.
const ADD_GROUPS = [
  { label: 'Text & Basics', match: (t) => ['text', 'rectangle', 'circle', 'line', 'chip', 'image'].includes(t) },
  { label: 'Shapes',        match: (t) => t.startsWith('shape_') },
  { label: 'Ribbons',       match: (t) => t.startsWith('ribbon_') },
  { label: 'Badges',        match: (t) => t.startsWith('badge_') },
  { label: 'Bows',          match: (t) => t.startsWith('bow_') },
  { label: 'Icons',         match: (t) => t.startsWith('icon_') },
  { label: 'Effects',       match: (t) => t.startsWith('effect_') },
];

// ── History (undo/redo) — session-scoped, in-memory only ─────
function useHistory(initial) {
  const [state, setState] = useState(initial);
  const past = useRef([]);
  const future = useRef([]);
  const set = useCallback((next, opts = {}) => {
    setState(prev => {
      if (!opts.silent) {
        past.current.push(JSON.stringify(prev));
        if (past.current.length > 60) past.current.shift();
        future.current = [];
      }
      const val = typeof next === 'function' ? next(prev) : next;
      return val;
    });
  }, []);
  const undo = useCallback(() => {
    if (past.current.length === 0) return;
    setState(prev => {
      future.current.push(JSON.stringify(prev));
      return JSON.parse(past.current.pop());
    });
  }, []);
  const redo = useCallback(() => {
    if (future.current.length === 0) return;
    setState(prev => {
      past.current.push(JSON.stringify(prev));
      return JSON.parse(future.current.pop());
    });
  }, []);
  const reset = useCallback((val) => {
    past.current = []; future.current = [];
    setState(val);
  }, []);
  return { state, set, undo, redo, reset,
           canUndo: past.current.length > 0,
           canRedo: future.current.length > 0 };
}

// ── Utility ────────────────────────────────────────────────────
const clone = (o) => JSON.parse(JSON.stringify(o));
const findEl = (design, id) => (design?.elements || []).find(e => e.id === id) || null;

// ══════════════════════════════════════════════════════════════
//                          EDITOR
// ══════════════════════════════════════════════════════════════
export default function VoucherDesignEditor({
  initialDesign,      // may be legacy V1 or V2 or null
  voucher,            // sample voucher for live preview
  outlet,             // { name, tagline, logo, initials }
  onSave,             // (design) => Promise<void>
  onChange,           // (design) => void — streams every local mutation up
  onClose,
  saving = false,
}) {
  const seed = useMemo(() => {
    if (initialDesign?.design_version === 2 && Array.isArray(initialDesign.elements)) {
      return clone(initialDesign);
    }
    // legacy → V2 conversion (non-destructive: preserved in _legacy)
    return migrateLegacyToV2(initialDesign || {}, initialDesign?.theme || 'dark_green_gold');
  }, [initialDesign]);

  const { state: design, set: setDesign, undo, redo, reset } = useHistory(seed);
  const [selectedId, setSelectedId] = useState(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);

  // Stream every local design mutation up to the parent — the parent
  // Builder relies on this to keep its own `design` state in sync so a
  // Save Voucher click always persists the LIVE canvas, not a stale copy.
  useEffect(() => { onChange?.(design); }, [design, onChange]);

  // Keyboard: Delete, Ctrl+Z, Ctrl+Shift+Z / Ctrl+Y.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      else if (((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'z')
              || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y')) { e.preventDefault(); redo(); }
      else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        e.preventDefault(); deleteSelected();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, undo, redo]);

  // ── Element mutations (routed through history) ─────────────
  const mutateElement = useCallback((id, patch) => {
    setDesign(prev => {
      const next = clone(prev);
      const idx = next.elements.findIndex(e => e.id === id);
      if (idx < 0) return prev;
      next.elements[idx] = clampElement({ ...next.elements[idx], ...patch }, {});
      return next;
    });
  }, [setDesign]);

  // Drag/resize come as rapid patches — coalesce into ONE history entry
  // by using `silent` while dragging, then commit once on drop.
  const dragActive = useRef(false);
  const liveMutate = useCallback((id, patch) => {
    if (!dragActive.current) {
      dragActive.current = true;
      // First patch of a drag pushes a history snapshot; subsequent
      // patches inside the same drag are silent.
      setDesign(prev => {
        const next = clone(prev);
        const idx = next.elements.findIndex(e => e.id === id);
        if (idx < 0) return prev;
        next.elements[idx] = clampElement({ ...next.elements[idx], ...patch }, {});
        return next;
      });
      const commit = () => { dragActive.current = false; window.removeEventListener('mouseup', commit); };
      window.addEventListener('mouseup', commit);
    } else {
      setDesign(prev => {
        const next = clone(prev);
        const idx = next.elements.findIndex(e => e.id === id);
        if (idx < 0) return prev;
        next.elements[idx] = clampElement({ ...next.elements[idx], ...patch }, {});
        return next;
      }, { silent: true });
    }
  }, [setDesign]);

  const patchStyle = (id, stylePatch) => {
    setDesign(prev => {
      const next = clone(prev);
      const idx = next.elements.findIndex(e => e.id === id);
      if (idx < 0) return prev;
      next.elements[idx].style = { ...(next.elements[idx].style || {}), ...stylePatch };
      return next;
    });
  };
  const patchContent = (id, contentPatch) => {
    setDesign(prev => {
      const next = clone(prev);
      const idx = next.elements.findIndex(e => e.id === id);
      if (idx < 0) return prev;
      next.elements[idx].content = { ...(next.elements[idx].content || {}), ...contentPatch };
      return next;
    });
  };
  const patchCanvas = (canvasPatch) => {
    setDesign(prev => {
      const next = clone(prev);
      next.canvas = { ...(next.canvas || {}), ...canvasPatch };
      return next;
    });
  };

  const addElement = (factory) => {
    setDesign(prev => {
      const next = clone(prev);
      const el = factory();
      next.elements = [...(next.elements || []), el];
      setTimeout(() => setSelectedId(el.id), 0);
      return next;
    });
    setAddMenuOpen(false);
  };
  const deleteSelected = () => {
    if (!selectedId) return;
    // Phase 2A · every layer is deletable.
    setDesign(prev => {
      const next = clone(prev);
      next.elements = (next.elements || []).filter(e => e.id !== selectedId);
      return next;
    });
    setSelectedId(null);
  };
  const duplicateSelected = () => {
    if (!selectedId) return;
    const el = findEl(design, selectedId);
    if (!el) return;
    // Phase 2A · dynamic bindings can also be duplicated deliberately.
    setDesign(prev => {
      const next = clone(prev);
      const copy = { ...clone(el),
                     // Give bindings a fresh id when duplicated so both
                     // instances remain independent layers. Decorative
                     // types already use unique ids per instance.
                     id: FUNCTIONAL_IDS.has(el.id) ? `${el.type}_${newElementId(el.type).split('_').slice(-1)[0]}` : newElementId(el.type),
                     x: Math.min(0.9, el.x + 0.03),
                     y: Math.min(0.9, el.y + 0.03),
                     z_index: (el.z_index || 5) + 1 };
      next.elements.push(copy);
      setTimeout(() => setSelectedId(copy.id), 0);
      return next;
    });
  };
  const bringForward = () => {
    if (!selectedId) return;
    setDesign(prev => {
      const next = clone(prev);
      const el = next.elements.find(e => e.id === selectedId);
      if (el) el.z_index = (el.z_index || 5) + 1;
      return next;
    });
  };
  const sendBackward = () => {
    if (!selectedId) return;
    setDesign(prev => {
      const next = clone(prev);
      const el = next.elements.find(e => e.id === selectedId);
      if (el) el.z_index = Math.max(0, (el.z_index || 5) - 1);
      return next;
    });
  };
  const centerHorizontal = () => {
    if (!selectedId) return;
    const el = findEl(design, selectedId);
    if (!el) return;
    mutateElement(el.id, { x: 0.5 - el.width / 2 });
  };
  const centerVertical = () => {
    if (!selectedId) return;
    const el = findEl(design, selectedId);
    if (!el) return;
    mutateElement(el.id, { y: 0.5 - el.height / 2 });
  };
  const toggleLock = (id) => {
    setDesign(prev => {
      const next = clone(prev);
      const el = next.elements.find(e => e.id === id);
      if (el) el.locked = !el.locked;
      return next;
    });
  };
  const toggleHidden = (id) => {
    setDesign(prev => {
      const next = clone(prev);
      const el = next.elements.find(e => e.id === id);
      if (!el) return prev;
      // Phase 2A · every layer is hideable.
      el.hidden = !el.hidden;
      return next;
    });
  };
  const renameLayer = (id, name) => {
    setDesign(prev => {
      const next = clone(prev);
      const el = next.elements.find(e => e.id === id);
      if (el) el.name = name;
      return next;
    });
  };
  const moveLayer = (id, dir) => {
    // Layer list is z-ordered ASC → moving DOWN in list = bringForward.
    if (dir === 'up')   bringForward();
    if (dir === 'down') sendBackward();
  };

  const applyPreset = (preset) => {
    // Land the preset transition in the history stack so Undo/Redo work.
    setDesign(preset.build());
    setSelectedId(null);
  };

  const selected = findEl(design, selectedId);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr_320px] gap-3"
         data-testid="voucher-composer">
      {/* ── LEFT · Presets + Layers ────────────────────────────── */}
      <div className="space-y-3">
        <Card className="p-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Presets</div>
          <div className="grid grid-cols-2 gap-2">
            {[BLANK_PRESET, ...COMPOSER_PRESETS].map(p => (
              <button key={p.key}
                      onClick={() => applyPreset(p)}
                      data-testid={`preset-${p.key}`}
                      className="rounded-md overflow-hidden ring-1 ring-slate-200 hover:ring-amber-400 transition text-left">
                <div style={{ height: 44, background: p.thumbnail_bg,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: 'rgba(255,255,255,0.65)', fontSize: 10, letterSpacing: '0.15em' }}>
                  {p.key === 'blank' ? 'START BLANK' : ''}
                </div>
                <div className="px-2 py-1 text-[11px] font-medium text-slate-700">{p.label}</div>
              </button>
            ))}
          </div>
        </Card>
        <LayersPanel design={design}
                     selectedId={selectedId}
                     onSelect={setSelectedId}
                     onToggleLock={toggleLock}
                     onToggleHidden={toggleHidden}
                     onRename={renameLayer}
                     onMoveUp={() => moveLayer(selectedId, 'up')}
                     onMoveDown={() => moveLayer(selectedId, 'down')} />
      </div>

      {/* ── CENTRE · Canvas ────────────────────────────────────── */}
      <div className="space-y-3">
        <Toolbar
          onUndo={undo} onRedo={redo}
          onDelete={deleteSelected}
          onDuplicate={duplicateSelected}
          onBringForward={bringForward}
          onSendBackward={sendBackward}
          onCenterX={centerHorizontal}
          onCenterY={centerVertical}
          onSave={() => onSave?.(design)}
          onAdd={() => setAddMenuOpen(v => !v)}
          addOpen={addMenuOpen}
          canDelete={!!selectedId}
          canDuplicate={!!selectedId}
          selectedId={selectedId}
          saving={saving}
        />
        {addMenuOpen && (
          <Card className="p-2 space-y-2" data-testid="add-menu">
            {/* Dynamic-field group: business bindings that can be
                added to the canvas. If a binding is already present,
                the tile shows a subtle "In design" pill. */}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Dynamic Fields</div>
              <div className="flex flex-wrap gap-2">
                {DYNAMIC_FIELD_ORDER.map(k => {
                  const already = (design.elements || []).some(e => e.id === k);
                  return (
                    <button key={k}
                            onClick={() => {
                              if (already) return;
                              addElement(makeDynamic[k]);
                            }}
                            disabled={already}
                            data-testid={`add-dyn-${k}`}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-xs
                                        ${already ? 'border-slate-200 opacity-40 cursor-not-allowed'
                                                  : 'border-slate-200 hover:border-amber-400'}`}>
                      <Plus className="w-3.5 h-3.5" /> {DYNAMIC_FIELD_LABELS[k]}
                      {already && <span className="text-[9px] text-slate-400 ml-1">· in design</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            {ADD_GROUPS.map(g => (
              <div key={g.label}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{g.label}</div>
                <div className="flex flex-wrap gap-2">
                  {ADDABLE.filter(a => g.match(a.type)).map(a => (
                    <button key={a.type}
                            onClick={() => addElement(a.factory)}
                            data-testid={`add-${a.type}`}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-slate-200 hover:border-amber-400 text-xs">
                      <a.Icon className="w-3.5 h-3.5" /> {a.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </Card>
        )}
        <div className="flex justify-center py-6 bg-slate-100 rounded-lg">
          <VoucherDesignRenderer
            design={design}
            voucher={voucher}
            outlet={outlet}
            validUntil={voucher?.valid_until || null}
            qrValue={voucher?.code || 'PREVIEW'}
            mode="edit"
            size="full"
            selectedId={selectedId}
            onSelect={setSelectedId}
            onMutate={liveMutate}
            onCanvasClick={() => setSelectedId(null)}
          />
        </div>
      </div>

      {/* ── RIGHT · Property panel ───────────────────────────── */}
      <div>
        <PropertyPanel
          selected={selected}
          canvas={design.canvas}
          onPatchStyle={patchStyle}
          onPatchContent={patchContent}
          onPatchCanvas={patchCanvas}
          onPatchElement={mutateElement}
        />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//                        TOOLBAR
// ══════════════════════════════════════════════════════════════
function Toolbar({ onUndo, onRedo, onDelete, onDuplicate, onBringForward,
                   onSendBackward, onCenterX, onCenterY, onSave, onAdd,
                   addOpen, canDelete, canDuplicate, selectedId, saving }) {
  const btn = "h-8 px-2 rounded-md border border-slate-200 hover:border-amber-400 text-slate-700 hover:text-slate-900 text-xs flex items-center gap-1 bg-white disabled:opacity-40 disabled:cursor-not-allowed";
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="composer-toolbar">
      <button className={btn} onClick={onUndo}   data-testid="toolbar-undo"    title="Undo (Ctrl+Z)"><Undo2 className="w-3.5 h-3.5" />Undo</button>
      <button className={btn} onClick={onRedo}   data-testid="toolbar-redo"    title="Redo"><Redo2 className="w-3.5 h-3.5" />Redo</button>
      <div className="w-px h-5 bg-slate-200 mx-1" />
      <button className={btn} onClick={onAdd}    data-testid="toolbar-add"     title="Add element">
        <Plus className="w-3.5 h-3.5" />{addOpen ? 'Close' : 'Add'}
      </button>
      <button className={btn} onClick={onDuplicate} disabled={!canDuplicate} data-testid="toolbar-duplicate" title="Duplicate">
        <Copy className="w-3.5 h-3.5" />Duplicate
      </button>
      <button className={btn} onClick={onDelete} disabled={!canDelete} data-testid="toolbar-delete" title="Delete">
        <Trash2 className="w-3.5 h-3.5" />Delete
      </button>
      <div className="w-px h-5 bg-slate-200 mx-1" />
      <button className={btn} onClick={onBringForward} disabled={!selectedId} data-testid="toolbar-forward"><ArrowUpFromLine className="w-3.5 h-3.5" />Forward</button>
      <button className={btn} onClick={onSendBackward} disabled={!selectedId} data-testid="toolbar-backward"><ArrowDownToLine className="w-3.5 h-3.5" />Backward</button>
      <div className="w-px h-5 bg-slate-200 mx-1" />
      <button className={btn} onClick={onCenterX} disabled={!selectedId} data-testid="toolbar-center-x" title="Center horizontally"><AlignHorizontalJustifyCenter className="w-3.5 h-3.5" /></button>
      <button className={btn} onClick={onCenterY} disabled={!selectedId} data-testid="toolbar-center-y" title="Center vertically"><AlignVerticalJustifyCenter className="w-3.5 h-3.5" /></button>
      <div className="flex-1" />
      <Button size="sm" onClick={onSave} disabled={saving}
              data-testid="composer-save"
              className="bg-amber-500 hover:bg-amber-600 text-white">
        {saving ? 'Saving…' : 'Save Design'}
      </Button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//                        LAYERS
// ══════════════════════════════════════════════════════════════
// Friendly labels for dynamic-binding elements — surfaces in the
// Layers panel and property panel headings so admins never see raw
// system ids like "brand_a1b2c3" as the primary label.
const _DYN_NAMES = {
  brand: 'Brand Name', logo: 'Brand Logo',
  title: 'Voucher Name', subtitle: 'Subtitle',
  value: 'Offer / Value', qr: 'QR Code',
  code: 'Voucher Code', validity: 'Validity',
  terms_cta: 'Terms CTA',
};
function _layerLabel(el) {
  if (el?.name) return el.name;
  if (_DYN_NAMES[el?.id]) return _DYN_NAMES[el.id];
  if (el?.content?.text) return el.content.text;
  // decorative types get a friendly type-prefix instead of the raw uuid.
  const t = (el?.type || '').replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}
function LayersPanel({ design, selectedId, onSelect, onToggleLock, onToggleHidden,
                       onRename, onMoveUp, onMoveDown }) {
  // Display in DESC z-index (top of the stack first) — Photoshop style.
  const els = (design?.elements || []).slice().sort((a, b) => (b.z_index || 0) - (a.z_index || 0));
  const [editingId, setEditingId] = useState(null);
  const [editVal, setEditVal] = useState('');
  return (
    <Card className="p-3" data-testid="layers-panel">
      <div className="flex justify-between items-center mb-2">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Layers</div>
        <div className="flex gap-1">
          <button onClick={onMoveUp} data-testid="layer-up" className="p-1 text-slate-500 hover:text-slate-900" title="Move layer up"><ArrowUpFromLine className="w-3 h-3" /></button>
          <button onClick={onMoveDown} data-testid="layer-down" className="p-1 text-slate-500 hover:text-slate-900" title="Move layer down"><ArrowDownToLine className="w-3 h-3" /></button>
        </div>
      </div>
      <div className="space-y-1 max-h-[380px] overflow-y-auto">
        {els.map(el => {
          const active = el.id === selectedId;
          const isEditing = editingId === el.id;
          const label = _layerLabel(el);
          return (
            <div key={el.id}
                 data-testid={`layer-row-${el.id}`}
                 className={`flex items-center gap-1.5 px-1.5 py-1 rounded text-xs cursor-pointer
                             ${active ? 'bg-amber-50 ring-1 ring-amber-300' : 'hover:bg-slate-50'}`}
                 onClick={() => onSelect(el.id)}
                 onDoubleClick={() => { setEditingId(el.id); setEditVal(label); }}>
              <span className="w-1.5 h-1.5 rounded-full"
                    style={{ background: FUNCTIONAL_IDS.has(el.id) ? '#0EA5E9' : '#94A3B8' }} />
              <div className="min-w-0 flex-1 truncate">
                {isEditing ? (
                  <input autoFocus className="w-full bg-white border border-slate-200 rounded px-1 py-0.5 text-xs"
                         value={editVal}
                         onChange={e => setEditVal(e.target.value)}
                         onBlur={() => { onRename(el.id, editVal.trim() || el.id); setEditingId(null); }}
                         onKeyDown={e => { if (e.key === 'Enter') { onRename(el.id, editVal.trim() || el.id); setEditingId(null); } }} />
                ) : (
                  <span className="truncate block">{label}</span>
                )}
              </div>
              <button onClick={(e) => { e.stopPropagation(); onToggleHidden(el.id); }}
                      className="p-0.5 text-slate-500 hover:text-slate-900"
                      data-testid={`layer-hide-${el.id}`}
                      title="Hide/Show">
                {el.hidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              </button>
              <button onClick={(e) => { e.stopPropagation(); onToggleLock(el.id); }}
                      className="p-0.5 text-slate-500 hover:text-slate-900"
                      data-testid={`layer-lock-${el.id}`}
                      title={el.locked ? 'Unlock' : 'Lock'}>
                {el.locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
              </button>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════
//                    PROPERTY PANEL
// ══════════════════════════════════════════════════════════════
function PropertyPanel({ selected, canvas, onPatchStyle, onPatchContent,
                         onPatchCanvas, onPatchElement }) {
  if (!selected) {
    return (
      <Card className="p-3" data-testid="property-panel">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Canvas</div>
        <CanvasProps canvas={canvas} onPatch={onPatchCanvas} />
      </Card>
    );
  }
  return (
    <Card className="p-3 space-y-3" data-testid="property-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Element</div>
          <div className="text-sm font-semibold">{_layerLabel(selected)}</div>
          <div className="text-[10px] text-slate-400">{selected.type}</div>
        </div>
        {FUNCTIONAL_IDS.has(selected.id) && (
          <span className="text-[10px] bg-sky-50 text-sky-700 px-1.5 py-0.5 rounded ring-1 ring-sky-200">
            functional
          </span>
        )}
      </div>

      {/* Common: opacity, rotation, z-index */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-slate-500">Opacity</Label>
          <input type="range" min="0" max="1" step="0.05"
                 value={selected.opacity ?? 1}
                 onChange={e => onPatchElement(selected.id, { opacity: parseFloat(e.target.value) })}
                 className="w-full" data-testid="prop-opacity" />
        </div>
        <div>
          <Label className="text-[10px] text-slate-500">Rotation</Label>
          <input type="number" step="1"
                 value={selected.rotation || 0}
                 onChange={e => onPatchElement(selected.id, { rotation: parseInt(e.target.value || 0, 10) })}
                 className="w-full border border-slate-200 rounded px-2 py-1 text-xs"
                 data-testid="prop-rotation" />
        </div>
      </div>

      {/* Type-specific */}
      {['text', 'title', 'subtitle', 'value', 'code', 'validity', 'terms_cta', 'brand'].includes(selected.type) && (
        <TextProps el={selected} onPatchStyle={onPatchStyle} onPatchContent={onPatchContent} />
      )}
      {['rectangle', 'circle'].includes(selected.type) && (
        <ShapeProps el={selected} onPatchStyle={onPatchStyle} />
      )}
      {['line', 'divider'].includes(selected.type) && (
        <LineProps el={selected} onPatchStyle={onPatchStyle} />
      )}
      {selected.type === 'image' && (
        <ImageProps el={selected} onPatchStyle={onPatchStyle} onPatchContent={onPatchContent} />
      )}
      {selected.type === 'chip' && (
        <ChipProps el={selected} onPatchStyle={onPatchStyle} onPatchContent={onPatchContent} />
      )}
      {selected.type === 'logo' && (
        <LogoProps el={selected} onPatchStyle={onPatchStyle} />
      )}
      {selected.type === 'qr' && (
        <QRProps el={selected} onPatchStyle={onPatchStyle} />
      )}
      {selected.type === 'ribbon' && (
        <RibbonProps el={selected} onPatchStyle={onPatchStyle} onPatchContent={onPatchContent} />
      )}
      {selected.type === 'badge' && (
        <BadgeProps el={selected} onPatchStyle={onPatchStyle} onPatchContent={onPatchContent} />
      )}
      {selected.type === 'icon' && (
        <IconProps el={selected} onPatchStyle={onPatchStyle} onPatchContent={onPatchContent} />
      )}
      {selected.type === 'effect' && (
        <EffectProps el={selected} onPatchStyle={onPatchStyle} />
      )}
      {selected.type === 'shape' && (
        <ShapeVariantProps el={selected} onPatchStyle={onPatchStyle} />
      )}
      {selected.type === 'bow' && (
        <BowProps el={selected} onPatchStyle={onPatchStyle} />
      )}
    </Card>
  );
}

// ── Ribbon / Badge / Icon / Effect property panels ─────────────
const RIBBON_VARIANTS = [
  { key: 'horizontal',   label: 'Horizontal' },
  { key: 'banner',       label: 'Banner (arrow)' },
  { key: 'tail',         label: 'Tail (notched)' },
  { key: 'corner',       label: 'Corner' },
  { key: 'folded',       label: 'Folded' },
  { key: 'festival',     label: 'Festival' },
  { key: 'luxury_strip', label: 'Luxury strip' },
  { key: 'sash',         label: 'Sash' },
  { key: 'pennant',      label: 'Pennant' },
];
function RibbonProps({ el, onPatchStyle, onPatchContent }) {
  const s = el.style || {};
  return (
    <div className="space-y-2">
      <div>
        <Label className="text-[10px] text-slate-500">Ribbon text</Label>
        <Input value={el.content?.text || ''}
                onChange={e => onPatchContent(el.id, { text: e.target.value })}
                className="text-xs h-8" data-testid="prop-ribbon-text" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[10px] text-slate-500">Style</Label>
             <Select value={s.variant || 'banner'}
                      onValueChange={v => onPatchStyle(el.id, { variant: v })}>
               <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
               <SelectContent>{RIBBON_VARIANTS.map(v => <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>)}</SelectContent>
             </Select></div>
        <div><Label className="text-[10px] text-slate-500">Fill</Label>
             <Input type="color" value={colourHex(s.fill)}
                    onChange={e => onPatchStyle(el.id, { fill: e.target.value })} className="h-8 p-1" /></div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[10px] text-slate-500">Secondary fill</Label>
             <Input type="color" value={colourHex(s.fill2)}
                    onChange={e => onPatchStyle(el.id, { fill2: e.target.value })} className="h-8 p-1" /></div>
        <div><Label className="text-[10px] text-slate-500">Text colour</Label>
             <Input type="color" value={colourHex(s.color)}
                    onChange={e => onPatchStyle(el.id, { color: e.target.value })} className="h-8 p-1" /></div>
      </div>
    </div>
  );
}
const BADGE_SHAPES = [
  { key: 'seal',       label: 'Seal' },
  { key: 'shield',     label: 'Shield' },
  { key: 'star',       label: 'Star' },
  { key: 'starburst',  label: 'Starburst' },
  { key: 'circle',     label: 'Circle' },
  { key: 'pill',       label: 'Pill' },
];
function BadgeProps({ el, onPatchStyle, onPatchContent }) {
  const s = el.style || {};
  return (
    <div className="space-y-2">
      <div>
        <Label className="text-[10px] text-slate-500">Badge text</Label>
        <Input value={el.content?.text || ''}
                onChange={e => onPatchContent(el.id, { text: e.target.value })}
                className="text-xs h-8" data-testid="prop-badge-text" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[10px] text-slate-500">Shape</Label>
             <Select value={s.shape || 'seal'}
                      onValueChange={v => onPatchStyle(el.id, { shape: v })}>
               <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
               <SelectContent>{BADGE_SHAPES.map(v => <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>)}</SelectContent>
             </Select></div>
        <div><Label className="text-[10px] text-slate-500">Fill</Label>
             <Input type="color" value={colourHex(s.bg)}
                    onChange={e => onPatchStyle(el.id, { bg: e.target.value })} className="h-8 p-1" /></div>
      </div>
      <div>
        <Label className="text-[10px] text-slate-500">Text colour</Label>
        <Input type="color" value={colourHex(s.color)}
                onChange={e => onPatchStyle(el.id, { color: e.target.value })} className="h-8 p-1" />
      </div>
    </div>
  );
}
const ICON_NAMES = ['Crown', 'Star', 'Gift', 'Sparkles', 'Utensils', 'Cake', 'Wine',
                    'Coffee', 'Heart', 'Ticket', 'Calendar', 'PartyPopper', 'Trophy',
                    'Diamond', 'Flame', 'Hotel', 'Bed', 'Flower2', 'ChefHat', 'Music',
                    'Percent', 'IndianRupee'];
function IconProps({ el, onPatchStyle, onPatchContent }) {
  const s = el.style || {};
  return (
    <div className="space-y-2">
      <div>
        <Label className="text-[10px] text-slate-500">Icon</Label>
        <Select value={el.content?.name || 'Star'}
                 onValueChange={v => onPatchContent(el.id, { name: v })}>
          <SelectTrigger className="h-8 text-xs" data-testid="prop-icon-name"><SelectValue /></SelectTrigger>
          <SelectContent>{ICON_NAMES.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[10px] text-slate-500">Colour</Label>
             <Input type="color" value={colourHex(s.color)}
                    onChange={e => onPatchStyle(el.id, { color: e.target.value })} className="h-8 p-1" /></div>
        <div><Label className="text-[10px] text-slate-500">Stroke</Label>
             <input type="range" min="0.5" max="3" step="0.1"
                     value={s.stroke_width || 1.5}
                     onChange={e => onPatchStyle(el.id, { stroke_width: parseFloat(e.target.value) })}
                     className="w-full" /></div>
      </div>
    </div>
  );
}
function EffectProps({ el, onPatchStyle }) {
  const s = el.style || {};
  return (
    <div className="space-y-2">
      <div><Label className="text-[10px] text-slate-500">Variant</Label>
           <Select value={s.variant || 'glow'} onValueChange={v => onPatchStyle(el.id, { variant: v })}>
             <SelectTrigger className="h-8 text-xs" data-testid="prop-effect-variant"><SelectValue /></SelectTrigger>
             <SelectContent>
               <SelectItem value="glow">Soft Glow</SelectItem>
               <SelectItem value="shimmer">Shimmer</SelectItem>
               <SelectItem value="shine_strip">Shine strip</SelectItem>
               <SelectItem value="shadow">Vignette / Inner shadow</SelectItem>
               <SelectItem value="glass">Glass panel</SelectItem>
               <SelectItem value="highlight">Highlight</SelectItem>
               <SelectItem value="blur_blob">Blur blob</SelectItem>
               <SelectItem value="gradient_glow">Gradient glow</SelectItem>
               <SelectItem value="metallic">Metallic sheen</SelectItem>
             </SelectContent>
           </Select></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[10px] text-slate-500">Colour</Label>
             <Input type="color" value={colourHex(s.color)}
                    onChange={e => onPatchStyle(el.id, { color: e.target.value })} className="h-8 p-1" /></div>
        <div><Label className="text-[10px] text-slate-500">Colour 2</Label>
             <Input type="color" value={colourHex(s.color2)}
                    onChange={e => onPatchStyle(el.id, { color2: e.target.value })} className="h-8 p-1" /></div>
      </div>
    </div>
  );
}
// ── Shape / Bow property panels ────────────────────────────────
const SHAPE_VARIANTS = [
  { key: 'rectangle',       label: 'Rectangle' },
  { key: 'rounded_rect',    label: 'Rounded rectangle' },
  { key: 'triangle',        label: 'Triangle' },
  { key: 'ellipse',         label: 'Ellipse' },
  { key: 'pill',            label: 'Pill' },
  { key: 'polygon',         label: 'Polygon' },
  { key: 'diagonal_block',  label: 'Diagonal block' },
  { key: 'arc',             label: 'Arc' },
  { key: 'wave',            label: 'Wave' },
];
function ShapeVariantProps({ el, onPatchStyle }) {
  const s = el.style || {};
  return (
    <div className="space-y-2">
      <div><Label className="text-[10px] text-slate-500">Shape</Label>
           <Select value={s.variant || 'rectangle'} onValueChange={v => onPatchStyle(el.id, { variant: v })}>
             <SelectTrigger className="h-8 text-xs" data-testid="prop-shape-variant"><SelectValue /></SelectTrigger>
             <SelectContent>{SHAPE_VARIANTS.map(v => <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>)}</SelectContent>
           </Select></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[10px] text-slate-500">Fill</Label>
             <Input type="color" value={colourHex(s.fill)}
                    onChange={e => onPatchStyle(el.id, { fill: e.target.value })} className="h-8 p-1" data-testid="prop-shape-fill" /></div>
        <div><Label className="text-[10px] text-slate-500">Fill 2 (gradient)</Label>
             <Input type="color" value={colourHex(s.fill2)}
                    onChange={e => onPatchStyle(el.id, { fill2: e.target.value })} className="h-8 p-1" /></div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[10px] text-slate-500">Stroke</Label>
             <Input type="color" value={colourHex(s.stroke)}
                    onChange={e => onPatchStyle(el.id, { stroke: e.target.value })} className="h-8 p-1" /></div>
        <div><Label className="text-[10px] text-slate-500">Stroke width</Label>
             <input type="range" min="0" max="6" step="0.5"
                    value={s.stroke_width || 0}
                    onChange={e => onPatchStyle(el.id, { stroke_width: parseFloat(e.target.value) })}
                    className="w-full" /></div>
      </div>
      {(s.variant === 'rectangle' || s.variant === 'rounded_rect') && (
        <div><Label className="text-[10px] text-slate-500">Corner radius (px)</Label>
             <input type="range" min="0" max="80" step="1"
                    value={s.border_radius || 0}
                    onChange={e => onPatchStyle(el.id, { border_radius: parseInt(e.target.value, 10) })}
                    className="w-full" /></div>
      )}
      {s.variant === 'polygon' && (
        <div><Label className="text-[10px] text-slate-500">Sides ({s.sides || 6})</Label>
             <input type="range" min="3" max="12" step="1"
                    value={s.sides || 6}
                    onChange={e => onPatchStyle(el.id, { sides: parseInt(e.target.value, 10) })}
                    className="w-full" /></div>
      )}
    </div>
  );
}
function BowProps({ el, onPatchStyle }) {
  const s = el.style || {};
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[10px] text-slate-500">Bow colour</Label>
             <Input type="color" value={colourHex(s.fill)}
                    onChange={e => onPatchStyle(el.id, { fill: e.target.value })} className="h-8 p-1" data-testid="prop-bow-fill" /></div>
        <div><Label className="text-[10px] text-slate-500">Shadow tone</Label>
             <Input type="color" value={colourHex(s.fill2)}
                    onChange={e => onPatchStyle(el.id, { fill2: e.target.value })} className="h-8 p-1" /></div>
      </div>
    </div>
  );
}

// ── prop-sub-panels ────────────────────────────────────────────
const FONTS = ['Inter', 'Montserrat', 'Playfair Display', 'Cinzel', 'Cormorant Garamond',
               'Lato', 'Merriweather', 'Poppins', 'Space Grotesk', 'JetBrains Mono, monospace'];

function TextProps({ el, onPatchStyle, onPatchContent }) {
  const s = el.style || {};
  const canEditText = ['text', 'terms_cta'].includes(el.type);
  return (
    <div className="space-y-2">
      {canEditText && (
        <div>
          <Label className="text-[10px] text-slate-500">Text</Label>
          <Input value={el.content?.text || ''}
                 onChange={e => onPatchContent(el.id, { text: e.target.value })}
                 data-testid="prop-text" className="text-xs h-8" />
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-slate-500">Font</Label>
          <Select value={s.font || 'Inter'} onValueChange={v => onPatchStyle(el.id, { font: v })}>
            <SelectTrigger className="h-8 text-xs" data-testid="prop-font"><SelectValue /></SelectTrigger>
            <SelectContent>{FONTS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-slate-500">Weight</Label>
          <Select value={String(s.weight || 500)} onValueChange={v => onPatchStyle(el.id, { weight: parseInt(v, 10) })}>
            <SelectTrigger className="h-8 text-xs" data-testid="prop-weight"><SelectValue /></SelectTrigger>
            <SelectContent>{[300, 400, 500, 600, 700, 800, 900].map(w => <SelectItem key={w} value={String(w)}>{w}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-slate-500">Size (of card width)</Label>
          <input type="range" min="0.012" max="0.15" step="0.002"
                 value={s.size || 0.028}
                 onChange={e => onPatchStyle(el.id, { size: parseFloat(e.target.value) })}
                 className="w-full" data-testid="prop-size" />
        </div>
        <div>
          <Label className="text-[10px] text-slate-500">Alignment</Label>
          <Select value={s.align || 'center'} onValueChange={v => onPatchStyle(el.id, { align: v })}>
            <SelectTrigger className="h-8 text-xs" data-testid="prop-align"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="left">Left</SelectItem>
              <SelectItem value="center">Center</SelectItem>
              <SelectItem value="right">Right</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-slate-500">Colour</Label>
          <Input type="color" value={colourHex(s.color)}
                 onChange={e => onPatchStyle(el.id, { color: e.target.value })}
                 className="h-8 p-1" data-testid="prop-color" />
        </div>
        <div>
          <Label className="text-[10px] text-slate-500">Letter spacing</Label>
          <Select value={s.letter_spacing || 'normal'} onValueChange={v => onPatchStyle(el.id, { letter_spacing: v })}>
            <SelectTrigger className="h-8 text-xs" data-testid="prop-tracking"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="0.02em">Tight</SelectItem>
              <SelectItem value="0.06em">Wide</SelectItem>
              <SelectItem value="0.14em">Extra wide</SelectItem>
              <SelectItem value="0.24em">Ultra wide</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
function ShapeProps({ el, onPatchStyle }) {
  const s = el.style || {};
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[10px] text-slate-500">Fill</Label>
             <Input type="color" value={colourHex(s.fill)}
                    onChange={e => onPatchStyle(el.id, { fill: e.target.value })}
                    className="h-8 p-1" data-testid="prop-fill" /></div>
        <div><Label className="text-[10px] text-slate-500">Corner radius (px)</Label>
             <input type="range" min="0" max="60" step="1"
                    value={s.border_radius || 0}
                    onChange={e => onPatchStyle(el.id, { border_radius: parseInt(e.target.value, 10) })}
                    className="w-full" data-testid="prop-radius" /></div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[10px] text-slate-500">Stroke</Label>
             <Input type="color" value={colourHex(s.stroke)}
                    onChange={e => onPatchStyle(el.id, { stroke: e.target.value })}
                    className="h-8 p-1" data-testid="prop-stroke" /></div>
        <div><Label className="text-[10px] text-slate-500">Stroke width</Label>
             <input type="range" min="0" max="6" step="0.5"
                    value={s.stroke_width || 0}
                    onChange={e => onPatchStyle(el.id, { stroke_width: parseFloat(e.target.value) })}
                    className="w-full" data-testid="prop-stroke-width" /></div>
      </div>
    </div>
  );
}
function LineProps({ el, onPatchStyle }) {
  const s = el.style || {};
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[10px] text-slate-500">Colour</Label>
             <Input type="color" value={colourHex(s.color)}
                    onChange={e => onPatchStyle(el.id, { color: e.target.value })}
                    className="h-8 p-1" data-testid="prop-line-color" /></div>
        <div><Label className="text-[10px] text-slate-500">Thickness</Label>
             <input type="range" min="0.5" max="8" step="0.2"
                    value={s.thickness || 1}
                    onChange={e => onPatchStyle(el.id, { thickness: parseFloat(e.target.value) })}
                    className="w-full" data-testid="prop-line-thickness" /></div>
      </div>
      <div>
        <Label className="text-[10px] text-slate-500">Style</Label>
        <Select value={s.dash || 'solid'} onValueChange={v => onPatchStyle(el.id, { dash: v })}>
          <SelectTrigger className="h-8 text-xs" data-testid="prop-line-style"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="solid">Solid</SelectItem>
            <SelectItem value="dashed">Dashed</SelectItem>
            <SelectItem value="dotted">Dotted</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
function ImageProps({ el, onPatchStyle, onPatchContent }) {
  const s = el.style || {};
  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onPatchContent(el.id, { url: ev.target.result });
    reader.readAsDataURL(file);
  };
  return (
    <div className="space-y-2">
      <div>
        <Label className="text-[10px] text-slate-500">Replace image</Label>
        <input type="file" accept="image/*" onChange={onUpload}
               data-testid="prop-image-upload"
               className="text-xs w-full" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-slate-500">Fit</Label>
          <Select value={s.fit || 'cover'} onValueChange={v => onPatchStyle(el.id, { fit: v })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cover">Cover</SelectItem>
              <SelectItem value="contain">Contain</SelectItem>
              <SelectItem value="fill">Fill</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-slate-500">Corner radius (px)</Label>
          <input type="range" min="0" max="60" step="1"
                 value={s.border_radius || 0}
                 onChange={e => onPatchStyle(el.id, { border_radius: parseInt(e.target.value, 10) })}
                 className="w-full" />
        </div>
      </div>
    </div>
  );
}
function ChipProps({ el, onPatchStyle, onPatchContent }) {
  const s = el.style || {};
  return (
    <div className="space-y-2">
      <div>
        <Label className="text-[10px] text-slate-500">Label</Label>
        <Input value={el.content?.text || ''}
               onChange={e => onPatchContent(el.id, { text: e.target.value })}
               className="text-xs h-8" data-testid="prop-chip-text" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[10px] text-slate-500">Fill</Label>
             <Input type="color" value={colourHex(s.bg)}
                    onChange={e => onPatchStyle(el.id, { bg: e.target.value })} className="h-8 p-1" /></div>
        <div><Label className="text-[10px] text-slate-500">Text</Label>
             <Input type="color" value={colourHex(s.color)}
                    onChange={e => onPatchStyle(el.id, { color: e.target.value })} className="h-8 p-1" /></div>
      </div>
    </div>
  );
}
function LogoProps({ el, onPatchStyle }) {
  const s = el.style || {};
  return (
    <div className="space-y-2">
      <div><Label className="text-[10px] text-slate-500">Shape</Label>
           <Select value={String(s.border_radius === 999 ? 999 : (s.border_radius || 0))}
                   onValueChange={v => onPatchStyle(el.id, { border_radius: parseInt(v, 10) })}>
             <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
             <SelectContent>
               <SelectItem value="0">Square</SelectItem>
               <SelectItem value="12">Rounded</SelectItem>
               <SelectItem value="999">Circle</SelectItem>
             </SelectContent>
           </Select>
      </div>
      <div><Label className="text-[10px] text-slate-500">Fit</Label>
           <Select value={s.fit || 'contain'} onValueChange={v => onPatchStyle(el.id, { fit: v })}>
             <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
             <SelectContent>
               <SelectItem value="contain">Contain</SelectItem>
               <SelectItem value="cover">Cover</SelectItem>
             </SelectContent>
           </Select></div>
    </div>
  );
}
function QRProps({ el, onPatchStyle }) {
  const s = el.style || {};
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[10px] text-slate-500">Foreground</Label>
             <Input type="color" value={colourHex(s.fg)}
                    onChange={e => onPatchStyle(el.id, { fg: e.target.value })} className="h-8 p-1" data-testid="prop-qr-fg" /></div>
        <div><Label className="text-[10px] text-slate-500">Background</Label>
             <Input type="color" value={colourHex(s.bg)}
                    onChange={e => onPatchStyle(el.id, { bg: e.target.value })} className="h-8 p-1" data-testid="prop-qr-bg" /></div>
      </div>
      <div className="text-[10px] text-slate-500 leading-tight">
        QR minimum readable size is enforced automatically. Cannot be
        deleted or shrunk below the readability floor.
      </div>
    </div>
  );
}
const CANVAS_PATTERNS = [
  { key: 'none',       label: 'None' },
  { key: 'dots',       label: 'Dots' },
  { key: 'lines',      label: 'Diagonal lines' },
  { key: 'grid',       label: 'Grid' },
  { key: 'crosshatch', label: 'Cross-hatch' },
  { key: 'waves',      label: 'Waves' },
  { key: 'noise',      label: 'Noise' },
  { key: 'luxury',     label: 'Luxury' },
  { key: 'damask',     label: 'Damask' },
  { key: 'ornamental', label: 'Ornamental' },
  { key: 'filigree',   label: 'Filigree' },
  { key: 'stars',      label: 'Stars' },
];
const SPLIT_LAYOUTS = [
  { key: '50_50',           label: '50 / 50' },
  { key: '30_70',           label: '30 / 70' },
  { key: '70_30',           label: '70 / 30' },
  { key: 'vertical',        label: 'Vertical split' },
  { key: 'horizontal',      label: 'Horizontal split' },
  { key: 'diagonal',        label: 'Diagonal' },
  { key: 'diagonal_reverse',label: 'Diagonal (reverse)' },
  { key: 'corner_block',    label: 'Corner block' },
  { key: 'band_top',        label: 'Band · top' },
  { key: 'band_bottom',     label: 'Band · bottom' },
  { key: 'band_left',       label: 'Band · left' },
  { key: 'band_right',      label: 'Band · right' },
];
function CanvasProps({ canvas, onPatch }) {
  const bg = canvas?.background || { type: 'gradient', value: '' };
  const bgType = typeof bg === 'string' ? 'raw' : (bg.type || 'gradient');
  const stops = Array.isArray(bg.stops) ? bg.stops
             : (bgType === 'gradient' || bgType === 'radial'
                 ? [{ color: '#0E2C22', pos: 0 }, { color: '#143E30', pos: 100 }] : []);
  const border = canvas?.border || {};
  const pattern = canvas?.pattern || { key: 'none' };
  const aspect = canvas?.aspect_ratio || 1.9;

  const updateStop = (i, patch) => {
    const next = stops.slice();
    next[i] = { ...next[i], ...patch };
    onPatch({ background: { ...bg, stops: next, type: bgType === 'radial' ? 'radial' : 'gradient' } });
  };
  const addStop = () => {
    const next = [...stops, { color: '#C9A227', pos: 50 }];
    onPatch({ background: { ...bg, stops: next, type: bgType === 'radial' ? 'radial' : 'gradient' } });
  };
  const removeStop = (i) => {
    if (stops.length <= 2) return;
    const next = stops.filter((_, idx) => idx !== i);
    onPatch({ background: { ...bg, stops: next } });
  };

  return (
    <div className="space-y-3">
      {/* Aspect ratio */}
      <div>
        <Label className="text-[10px] text-slate-500">Aspect ratio</Label>
        <Select value={String(aspect)}
                 onValueChange={v => onPatch({ aspect_ratio: parseFloat(v) })}>
          <SelectTrigger className="h-8 text-xs" data-testid="canvas-aspect"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ASPECT_RATIOS.map(r => (
              <SelectItem key={r.key} value={String(r.value)}>
                {r.label} · {r.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input type="range" min="0.5" max="2.5" step="0.01"
                value={aspect}
                onChange={e => onPatch({ aspect_ratio: parseFloat(e.target.value) })}
                className="w-full mt-1" data-testid="canvas-aspect-slider" />
        <div className="text-[10px] text-slate-400 text-right">{Number(aspect).toFixed(2)}</div>
      </div>

      <div>
        <Label className="text-[10px] text-slate-500">Background type</Label>
        <Select value={bgType} onValueChange={v => {
          if (v === 'solid')       onPatch({ background: { type: 'solid', value: '#0E2C22' } });
          else if (v === 'radial') onPatch({ background: { type: 'radial', stops, angle: 0 } });
          else if (v === 'gradient') onPatch({ background: { type: 'gradient', stops, angle: 135 } });
          else if (v === 'split')  onPatch({ background: { type: 'split', layout: '50_50', color1: '#0E2C22', color2: '#D4A44A' } });
          else if (v === 'image')  onPatch({ background: { type: 'image', value: '', fit: 'cover', position: 'center', opacity: 1, blur: 0 } });
        }}>
          <SelectTrigger className="h-8 text-xs" data-testid="canvas-bg-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="solid">Solid colour</SelectItem>
            <SelectItem value="gradient">Linear gradient</SelectItem>
            <SelectItem value="radial">Radial gradient</SelectItem>
            <SelectItem value="split">Split composition</SelectItem>
            <SelectItem value="image">Image</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {bgType === 'solid' && (
        <div>
          <Label className="text-[10px] text-slate-500">Colour</Label>
          <Input type="color" value={colourHex(bg.value)}
                  onChange={e => onPatch({ background: { type: 'solid', value: e.target.value } })}
                  className="h-8 p-1" />
        </div>
      )}

      {(bgType === 'gradient' || bgType === 'radial') && (
        <div className="space-y-2">
          {bgType === 'gradient' && (
            <div>
              <Label className="text-[10px] text-slate-500">Angle · {bg.angle ?? 135}°</Label>
              <input type="range" min="0" max="360" step="1"
                      value={bg.angle ?? 135}
                      onChange={e => onPatch({ background: { ...bg, angle: parseInt(e.target.value, 10) } })}
                      className="w-full" />
            </div>
          )}
          <Label className="text-[10px] text-slate-500">Colour stops ({stops.length})</Label>
          {stops.map((st, i) => (
            <div key={i} className="flex items-center gap-1"
                 data-testid={`canvas-stop-${i}`}>
              <Input type="color" value={colourHex(st.color)}
                      onChange={e => updateStop(i, { color: e.target.value })}
                      className="h-8 w-14 p-1" />
              <input type="range" min="0" max="100" step="1"
                      value={st.pos ?? (i * (100 / Math.max(1, stops.length - 1)))}
                      onChange={e => updateStop(i, { pos: parseInt(e.target.value, 10) })}
                      className="flex-1" />
              <span className="w-8 text-[10px] text-right text-slate-500">{st.pos ?? '?'}%</span>
              {stops.length > 2 && (
                <button className="text-slate-400 hover:text-rose-600 px-1"
                         onClick={() => removeStop(i)}>×</button>
              )}
            </div>
          ))}
          {stops.length < 5 && (
            <button className="text-[11px] text-amber-600 hover:underline"
                     onClick={addStop} data-testid="canvas-stop-add">+ Add stop</button>
          )}
        </div>
      )}

      {bgType === 'split' && (
        <div className="space-y-2">
          <Label className="text-[10px] text-slate-500">Layout</Label>
          <Select value={bg.layout || '50_50'}
                   onValueChange={v => onPatch({ background: { ...bg, layout: v, type: 'split' } })}>
            <SelectTrigger className="h-8 text-xs" data-testid="canvas-split-layout"><SelectValue /></SelectTrigger>
            <SelectContent>{SPLIT_LAYOUTS.map(v => <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-[10px] text-slate-500">Colour 1</Label>
                 <Input type="color" value={colourHex(bg.color1)}
                        onChange={e => onPatch({ background: { ...bg, color1: e.target.value, type: 'split' } })}
                        className="h-8 p-1" data-testid="canvas-split-c1" /></div>
            <div><Label className="text-[10px] text-slate-500">Colour 2</Label>
                 <Input type="color" value={colourHex(bg.color2)}
                        onChange={e => onPatch({ background: { ...bg, color2: e.target.value, type: 'split' } })}
                        className="h-8 p-1" data-testid="canvas-split-c2" /></div>
          </div>
        </div>
      )}

      {bgType === 'image' && (
        <div className="space-y-2">
          <div>
            <Label className="text-[10px] text-slate-500">Image (data URL or absolute URL)</Label>
            <Input value={bg.value || ''}
                    onChange={e => onPatch({ background: { ...bg, type: 'image', value: e.target.value } })}
                    className="text-xs h-8" placeholder="https://…" />
            <input type="file" accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const reader = new FileReader();
                      reader.onload = ev => onPatch({ background: { ...bg, type: 'image', value: ev.target.result } });
                      reader.readAsDataURL(f);
                    }}
                    className="text-xs w-full mt-1" data-testid="canvas-bg-upload" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-slate-500">Fit</Label>
              <Select value={bg.fit || 'cover'} onValueChange={v => onPatch({ background: { ...bg, fit: v, type: 'image' } })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cover">Cover</SelectItem>
                  <SelectItem value="contain">Contain</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Position</Label>
              <Select value={bg.position || 'center'}
                       onValueChange={v => onPatch({ background: { ...bg, position: v, type: 'image' } })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['center', 'top', 'bottom', 'left', 'right', 'top left', 'top right', 'bottom left', 'bottom right'].map(p =>
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      <div className="border-t pt-2">
        <Label className="text-[10px] text-slate-500">Pattern overlay</Label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <Select value={pattern.key || 'none'}
                   onValueChange={v => onPatch({ pattern: { ...pattern, key: v } })}>
            <SelectTrigger className="h-8 text-xs" data-testid="canvas-pattern"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CANVAS_PATTERNS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="color" value={colourHex(pattern.color)}
                  onChange={e => onPatch({ pattern: { ...pattern, color: e.target.value } })}
                  className="h-8 p-1" />
        </div>
        {pattern.key && pattern.key !== 'none' && (
          <div className="mt-1">
            <Label className="text-[10px] text-slate-500">Opacity · {Math.round((pattern.opacity ?? 0.55) * 100)}%</Label>
            <input type="range" min="0" max="1" step="0.05"
                    value={pattern.opacity ?? 0.55}
                    onChange={e => onPatch({ pattern: { ...pattern, opacity: parseFloat(e.target.value) } })}
                    className="w-full" />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 border-t pt-2">
        <div>
          <Label className="text-[10px] text-slate-500">Border colour</Label>
          <Input type="color" value={colourHex(border.color)}
                  onChange={e => onPatch({ border: { ...border, color: e.target.value } })}
                  className="h-8 p-1" />
        </div>
        <div>
          <Label className="text-[10px] text-slate-500">Corner radius</Label>
          <input type="range" min="0" max="40" step="1"
                  value={border.radius ?? 20}
                  onChange={e => onPatch({ border: { ...border, radius: parseInt(e.target.value, 10) } })}
                  className="w-full" />
        </div>
      </div>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────
function colourHex(v) {
  // Coerce arbitrary CSS colour to #RRGGBB (best-effort for input[type=color]).
  if (!v) return '#000000';
  if (v.startsWith('#') && (v.length === 7 || v.length === 4)) {
    if (v.length === 4) return '#' + v.slice(1).split('').map(c => c + c).join('');
    return v;
  }
  const m = String(v).match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (m) {
    const h = (n) => Number(n).toString(16).padStart(2, '0');
    return `#${h(m[1])}${h(m[2])}${h(m[3])}`;
  }
  return '#000000';
}
